import { toJS } from "mobx";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useGlobalStore } from "../../api/globalStores";
import { Module } from "../../api/module";
import teacher from "../../api/teacher";
import ButtonPrimary from "../../Common/Buttons/ButtonPrimary";
import IconButtonPrimary from "../../Common/Buttons/IconButtonPrimary";
import BaseCard from "../../Common/Cards/BaseCard";
import Input from "../../Common/Forms/Input";
import Select from "../../Common/Forms/Select";
import IconPlus from "../../Common/Icons/IconPlus";
import Modal from "../../Common/Utility/Modal";
import { LessonColumns } from "../Components/Table/LessonColumns";
import Table, { ILessonDataRow } from "../Components/Table/Table";
import { definitions } from "../../api/supabase/client";

export interface ILessonPlanningPage {}

export default observer(LessonPlanningPage);

function LessonPlanningPage() {
  const globalStores = useGlobalStore();
  const [openCreateLessonDialog, setOpenCreateLessonDialog] = useState(false);
  const [openAddTopicDialog, setOpenAddTopicDialog] = useState(false);
  const [levels, setLevels] = useState<definitions["Level"][]>([]);
  const [topics, setTopics] = useState<definitions["Topic"][]>([]);
  const [modules, setModules] = useState<Module[]>([]);

  const navigate = useNavigate();

  const [newLessonPlanLevel, setNewLessonPlanLevel] = useState<
    number | undefined
  >();
  const [newTopic, setNewTopic] = useState<{ name?: string; levelId?: number }>(
    {}
  );
  const [newModule, setNewModule] = useState<{
    moduleName?: string;
    topicId?: number;
  }>({});

  const handleOpenCreateLessonDialog = () => {
    setOpenCreateLessonDialog(true);
  };

  const handleCloseCreateLessonDialog = () => {
    setOpenCreateLessonDialog(false);
  };

  const handleCreateLesson = () => {
    setOpenCreateLessonDialog(false);
    toast.promise(
      (async () => {
        if (
          newModule.topicId === undefined ||
          newModule.moduleName === undefined
        )
          throw new Error(
            "Level, Topic and Module Name must be selected to create a new Module!"
          );
        await teacher.addModule(newModule.topicId, newModule.moduleName);
        await globalStores.teacherStore.pullUserDetails();
        setModules(await globalStores.teacherStore.getModules());
      })(),
      {
        success: "Module added successfully!",
        error: (e) => e.message,
        loading: "Adding Module",
      }
    );
  };

  const handleCreateTopic = () => {
    setOpenAddTopicDialog(false);
    //TODO: Check edge case if fields are still filled in after closing and reopening but state has changed
    toast.promise(
      (async () => {
        if (newTopic.name === undefined || newTopic.levelId === undefined)
          throw new Error(
            "Both Name and Level must be selected to create a new Topic!"
          );
        await teacher.addTopic(newTopic.name, newTopic.levelId);
        setTopics(await globalStores.teacherStore.getTopics());
      })(),
      {
        success: "Topic added successfully!",
        error: (e) => e.message,
        loading: "Adding Topic",
      }
    );
  };

  useEffect(() => {
    const run = async () => {
      setLevels(await globalStores.teacherStore.getLevels());
      setTopics(await globalStores.teacherStore.getTopics());
      setModules(await globalStores.teacherStore.getModules());
    };

    run();
  }, [globalStores.teacherStore]);

  const curLevel = levels.find((level) => level.id === newLessonPlanLevel);

  return (
    <div className="flex flex-col gap-6 text-left h-full">
      <Modal
        open={openAddTopicDialog}
        onClose={() => setOpenAddTopicDialog(false)}
      >
        <BaseCard title="Create New Topic">
          <div>
            {curLevel
              ? `${curLevel.name} HSK Difficulty: ${curLevel.hskDifficulty}`
              : "Please select a level to add the topic to first!"}
          </div>
          {/* <Select
            icon="badge"
            placeholder="Choose Level"
            options={
              levels
                ? levels.map((level) => ({
                    value: level.id,
                    label: `${level.name} HSK Difficulty: ${level.hskDifficulty}`,
                  }))
                : []
            }
            onChange={(levelId) => {
              setNewTopic({ ...newTopic, levelId: +levelId });
            }}
          /> */}
          <Input
            type="text"
            placeholder="Topic Name"
            icon="book"
            onChange={(name) => {
              setNewTopic({ ...newTopic, name });
            }}
          />
          <ButtonPrimary onClick={handleCreateTopic}>Create</ButtonPrimary>
        </BaseCard>
      </Modal>
      <Modal
        open={openCreateLessonDialog}
        onClose={handleCloseCreateLessonDialog}
      >
        <BaseCard title="Create Lesson Plan" increaseHeight>
          <div className="w-full inline-flex flex-col gap-3">
            <Select
              icon="badge"
              placeholder="Choose Level"
              options={
                levels
                  ? levels.map((level) => ({
                      value: level.id,
                      label: `${level.name} HSK Difficulty: ${level.hskDifficulty}`,
                    }))
                  : []
              }
              onChange={(levelId) => {
                setNewTopic({ ...newTopic, levelId: +levelId });
                setNewLessonPlanLevel(+levelId);
              }}
            />
            <Select
              icon="book"
              placeholder="Choose Topic"
              options={
                topics
                  ? topics
                      .filter((topic) => topic.level === newLessonPlanLevel)
                      .map((topic) => ({
                        value: topic.id,
                        label: topic.name!,
                      }))
                  : []
              }
              onAddOption={() => {
                //TODO: Make sure if no level is selected we set edge case display for choosing a topic (Please select a topic first)
                setOpenAddTopicDialog(true);
              }}
              onChange={(topicId) =>
                setNewModule({ ...newModule, topicId: +topicId })
              }
            />
            <Input
              type="text"
              placeholder="Module"
              icon="puzzle"
              onChange={(moduleName) =>
                setNewModule({ ...newModule, moduleName })
              }
            />
          </div>
          <ButtonPrimary onClick={handleCreateLesson}>Create</ButtonPrimary>
        </BaseCard>
      </Modal>
      <div className="flex justify-between">
        <div className="text-heading1 font-semibold">Lesson Planning</div>
        <div className="stroke-brij-White">
          <IconButtonPrimary
            onClick={handleOpenCreateLessonDialog}
            text="New Lesson"
            icon={<IconPlus />}
            color="mud"
          />
        </div>
      </div>
      <Table
        rowData={modules.map((module) => {
          return {
            level: module.level.hskDifficulty ?? 0,
            topic: module.topic.name ?? "",
            module: module.module.name ?? "",
            //TODO: Make the date created into a human readable format
            date_created: module.module.inserted_at
              ? new Date(module.module.inserted_at).toLocaleString()
              : "",
            //TODO: Change the created by to teacher's name instead of id
            created_by: module.createdBy?.displayName ?? "",
            completed: module.module.isCompleted,
            notion_url:
              "https://brijedu.notion.site/Lesson-Planning-ad26eb4e1b9a48788d5f41ae4f45304f",
            //TODO: Can we keep everything camelcase
            fullModule: module,
          } as ILessonDataRow;
        })}
        colData={LessonColumns}
        onClickRow={async (row) => {
          await globalStores.curModule.clear();
          await globalStores.curEPanel.clear();
          navigate(
            `/teacher/epmaker?module=${row.fullModule.module.id}&epanel=${row.fullModule.engagementPanels[0]?.id}`,
            {}
          );
        }}
      />
    </div>
  );
}
